/**
 * Карточки базы знаний в панели справа — из «Вопросов клиентов» (списком и «По группам»), не уходя со списка.
 * Адрес: `&cards=<id,id…>&card=<открытая>` (назад в браузере закрывает), `&cg=<ключ группы>` — подсветка группы.
 * Карточек несколько (группа и её формулировки) — переключатель в шапке. Внутри — та же карточка, что на
 * `/knowledge/<id>` (`KnowledgeCardPage` с `id`): текст, правка, замечания, предложения, история.
 * Фона-затемнения нет, как у `HelpPanel`: панель читают, глядя на список.
 */
import { X } from "lucide-react";
import { useEffect } from "react";

import KnowledgeCardPage from "@/pages/KnowledgeCardPage";

export type PanelCard = { id: number; title: string };

export function CardPanel({
  cards,
  active,
  title,
  onPick,
  onClose,
}: {
  cards: PanelCard[];
  active: number;
  /** Откуда открыта: заголовок группы или текст вопроса. */
  title: string;
  onPick: (id: number) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <aside
      className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[min(44rem,100vw)] flex-col border-l border-border bg-bg shadow-xl"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      role="dialog"
      aria-label="Карточки базы знаний"
      data-annot="панель карточек"
    >
      <div className="border-b border-border px-4 py-2.5">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] text-fg-faint">{cards.length > 1 ? `Карточки по вопросам · ${cards.length}` : "Карточка"}</div>
            {title && !cards.some((c) => c.title === title) && (
              <div className="truncate text-[13px] text-fg-muted" title={title}>
                по вопросу: {title}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="rounded-[var(--radius-ui)] p-1 hover:bg-bg-hover" title="Закрыть (Esc)">
            <X size={16} aria-hidden />
            <span className="sr-only">Закрыть</span>
          </button>
        </div>
        {cards.length > 1 && (
          <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5" role="tablist" aria-label="Карточки">
            {cards.map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={c.id === active}
                className={`max-w-64 shrink-0 truncate rounded-full border px-2.5 py-1 text-[12px] ${
                  c.id === active ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"
                }`}
                title={c.title}
                onClick={() => onPick(c.id)}
              >
                {c.title}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <KnowledgeCardPage id={active} />
      </div>
    </aside>
  );
}
